<template>
  <div id="app" :class="isDark ? 'theme-dark' : 'theme-light'">
    <TheNavbar v-if="$route.name !== 'Login'" :is-dark="isDark" @toggle-theme="toggleTheme" />
    <router-view />
    <TheFooter v-if="showSiteFooter" :is-dark="isDark" />
    <FloatingBar v-if="showFloatingBar" />
  </div>
</template>

<script>
import TheNavbar from '@/components/TheNavbar.vue'
import TheFooter from '@/components/TheFooter.vue'
import FloatingBar from '@/components/FloatingBar.vue'

export default {
  name: 'App',
  components: { TheNavbar, TheFooter, FloatingBar },
  data() {
    return { isDark: false }
  },
  computed: {
    showSiteFooter() {
      const hidden = [
        'WorkflowStore',
        'WorkflowDetail',
        'AgentsCenter',
        'AgentUsage',
        'InspirationPlaza',
        'Login',
        'UserCenter',
        'DigitalEmployee',
        'DeAgentQuickEcom',
        'DeAgentQuickVideo',
        'DeAgentEcom',
        'DeAgentGeneral',
        'DeAgentVideo',
        'DeAgentAssets',
        'DeAgentInspiration',
        'EcomWorkflow',
        'GeneralWorkflow',
        'VideoWorkflow',
        'QuickEcom',
        'QuickVideo',
      ]
      return !hidden.includes(this.$route.name)
    },
    showFloatingBar() {
      if (this.$route.name === 'Login') return false
      const noBar = [
        'DigitalEmployee',
        'DeAgentQuickEcom',
        'DeAgentQuickVideo',
        'DeAgentEcom',
        'DeAgentGeneral',
        'DeAgentVideo',
        'DeAgentAssets',
        'DeAgentInspiration',
        'EcomWorkflow',
        'GeneralWorkflow',
        'VideoWorkflow',
        'QuickEcom',
        'QuickVideo',
      ]
      return !noBar.includes(this.$route.name)
    },
  },
  created() {
    const saved = localStorage.getItem('ds-theme')
    if (saved) {
      this.isDark = saved === 'dark'
    } else {
      this.isDark = window.matchMedia('(prefers-color-scheme: dark)').matches
    }
  },
  methods: {
    toggleTheme() {
      this.isDark = !this.isDark
      localStorage.setItem('ds-theme', this.isDark ? 'dark' : 'light')
    }
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap');

*, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

html { scroll-behavior: smooth; }

#app {
  font-family: 'Plus Jakarta Sans', 'PingFang SC', 'Microsoft YaHei', 'Noto Sans SC', sans-serif;
  transition: background-color 0.35s ease, color 0.35s ease;
  min-height: 100vh;
  overflow-x: hidden;
}

/* ====== LIGHT THEME ====== */
.theme-light {
  --bg: #F0F9FF;
  --bg-alt: #E0F2FE;
  --bg-card: #FFFFFF;
  --bg-nav: rgba(240, 249, 255, 0.92);
  --bg-section: #F8FBFF;
  --text: #0F172A;
  --text-secondary: #334155;
  --text-muted: #64748B;
  --primary: #2563EB;
  --primary-hover: #1D4ED8;
  --primary-light: #DBEAFE;
  --secondary: #0EA5E9;
  --accent: #06B6D4;
  --success: #10B981;
  --border: #BFDBFE;
  --border-light: #E0F2FE;
  --shadow-sm: 0 1px 3px rgba(37,99,235,0.08);
  --shadow: 0 4px 16px rgba(37,99,235,0.12);
  --shadow-lg: 0 12px 40px rgba(37,99,235,0.18);
  --gradient-hero: linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 50%, #F0F9FF 100%);
  --gradient-brand: linear-gradient(135deg, #2563EB 0%, #06B6D4 100%);
  --gradient-card: linear-gradient(145deg, #FFFFFF, #F0F9FF);
  --uc-sidebar-border: rgba(191, 219, 254, 0.5);
  --uc-sidebar-hover: #F0F9FF;
  background: var(--bg);
  color: var(--text);
}

/* ====== DARK THEME ====== */
.theme-dark {
  --bg: #050D1A;
  --bg-alt: #071628;
  --bg-card: #0D1B2E;
  --bg-nav: rgba(5, 13, 26, 0.92);
  --bg-section: #071220;
  --text: #F1F5F9;
  --text-secondary: #CBD5E1;
  --text-muted: #64748B;
  --primary: #3B82F6;
  --primary-hover: #2563EB;
  --primary-light: #1E3A5F;
  --secondary: #38BDF8;
  --accent: #22D3EE;
  --success: #34D399;
  --border: #1E3A5F;
  --border-light: #122240;
  --shadow-sm: 0 1px 3px rgba(0,0,0,0.3);
  --shadow: 0 4px 16px rgba(0,0,0,0.4);
  --shadow-lg: 0 12px 40px rgba(0,0,0,0.6);
  --gradient-hero: linear-gradient(135deg, #050D1A 0%, #071E40 50%, #050D1A 100%);
  --gradient-brand: linear-gradient(135deg, #1D4ED8 0%, #0EA5E9 100%);
  --gradient-card: linear-gradient(145deg, #0D1B2E, #0A1525);
  --uc-sidebar-border: rgba(30, 58, 95, 0.5);
  --uc-sidebar-hover: #050D1A;
  background: var(--bg);
  color: var(--text);
}

/* ====== GLOBAL UTILITIES ====== */
.container {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px;
}

.section { padding: 96px 0; }

.section-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 16px;
  background: var(--primary-light);
  color: var(--primary);
  border-radius: 100px;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  margin-bottom: 16px;
}

.section-title {
  font-size: clamp(1.75rem, 3vw, 2.75rem);
  font-weight: 800;
  text-align: center;
  color: var(--text);
  line-height: 1.2;
  margin-bottom: 1rem;
}

.section-subtitle {
  font-size: 1.05rem;
  text-align: center;
  color: var(--text-muted);
  max-width: 680px;
  margin: 0 auto 3rem;
  line-height: 1.8;
}

.gradient-text {
  background: var(--gradient-brand);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 32px;
  background: var(--gradient-brand);
  color: #fff;
  border-radius: 100px;
  font-weight: 600;
  font-size: 0.95rem;
  border: none;
  cursor: pointer;
  transition: all 0.25s ease;
  text-decoration: none;
  box-shadow: var(--shadow);
  white-space: nowrap;
}
.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-lg);
}

.btn-outline {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 13px 32px;
  background: transparent;
  color: var(--primary);
  border-radius: 100px;
  font-weight: 600;
  font-size: 0.95rem;
  border: 2px solid var(--primary);
  cursor: pointer;
  transition: all 0.25s ease;
  text-decoration: none;
  white-space: nowrap;
}
.btn-outline:hover {
  background: var(--primary);
  color: #fff;
  transform: translateY(-2px);
}

/* ====== ANIMATIONS ====== */
@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(32px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes fadeInLeft {
  from { opacity: 0; transform: translateX(-32px); }
  to { opacity: 1; transform: translateX(0); }
}
@keyframes fadeInRight {
  from { opacity: 0; transform: translateX(32px); }
  to { opacity: 1; transform: translateX(0); }
}
@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-16px); }
}
@keyframes floatSlow {
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-24px) rotate(5deg); }
}
@keyframes pulse-ring {
  0% { transform: scale(0.9); opacity: 1; }
  100% { transform: scale(1.6); opacity: 0; }
}
@keyframes marquee {
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
}
@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}
@keyframes countUp {
  from { opacity: 0; transform: scale(0.8); }
  to { opacity: 1; transform: scale(1); }
}

.visible { animation: fadeInUp 0.6s ease-out both; }

@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; }
}

@media (max-width: 768px) {
  .section { padding: 64px 0; }
  .container { padding: 0 16px; }
  .btn-primary, .btn-outline { padding: 12px 24px; font-size: 0.9rem; }
}

/* Scrollbar */
::-webkit-scrollbar { width: 6px; height: 6px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: var(--border); border-radius: 3px; }
::-webkit-scrollbar-thumb:hover { background: var(--primary); }
</style>
